import { App, AuthenticationError, AuthorizationError, ContainerLog, NetworkError, Subscription } from '@back4app2/sdk';
import { useEffect, useReducer } from 'react';
import { useOutletContext } from 'react-router-dom';
import back4app2 from '../back4app2';
import LoadingSpinner from '../components/LoadingSpinner';
import { BACK4APP_DOT_COM_SITE_URL } from '../settings';
import { AmplitudeEvent, trackEvent } from '../utils/amplitude';
import ContainerLogBox from '../components/ContainerLogBox';
import { Eyebrow, SurfaceCard } from '../components/ds';

interface MyAppLogsState {
  isLoading: boolean;
  loadingErrorMessage?: string;
  logs?: ContainerLog[];
  autoScroll: boolean;
}

const INITIAL_STATE: MyAppLogsState = {
  isLoading: true,
  autoScroll: true
};

enum MyAppLogsActionType {
  RESET,
  FINISH_LOADING,
  SET_AUTO_SCROLL
}

const reset = () => ({
  type: MyAppLogsActionType.RESET
} as const);

const finishLoading = (errorMessage?: string, logs?: ContainerLog[]) => ({
  type: MyAppLogsActionType.FINISH_LOADING,
  payload: {
    errorMessage,
    logs
  }
} as const);

const setAutoScroll = (value: boolean) => ({
  type: MyAppLogsActionType.SET_AUTO_SCROLL,
  payload: {
    value
  }
} as const);

type MyAppLogsAction = ReturnType<typeof reset> | ReturnType<typeof finishLoading> | ReturnType<typeof setAutoScroll>;

const reducer = (state: MyAppLogsState = INITIAL_STATE, action: MyAppLogsAction): MyAppLogsState => {
  switch (action.type) {
    case MyAppLogsActionType.RESET:
      return INITIAL_STATE;
    
    case MyAppLogsActionType.FINISH_LOADING:
      return {
        ...state,
        isLoading: false,
        loadingErrorMessage: action.payload.errorMessage,
        logs: action.payload.logs
      };

    case MyAppLogsActionType.SET_AUTO_SCROLL:
      return {
        ...state,
        autoScroll: action.payload.value
      };
  }
};

const MyAppLogs = () => {
  const { app } = useOutletContext<{ app: App }>();
  const mainServiceEnvironment = app && app.mainService && app.mainService.mainServiceEnvironment;
  const mainServiceEnvironmentId = mainServiceEnvironment && mainServiceEnvironment.id;
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  let { isLoading, loadingErrorMessage, logs } = state;

  useEffect(() => {
    trackEvent(AmplitudeEvent.AT_APP_LOGS_PAGE);
  }, []);

  useEffect(
    () => {
      let subscription: Subscription;

      if (mainServiceEnvironmentId) {
        subscription = back4app2.subscribeToContainersLogs(
          mainServiceEnvironmentId,
          (error, snapshot) => {
            if (error) {
              if (error instanceof NetworkError) {
                console.error('network error', error);
                dispatch(finishLoading('Network error when loading app logs. Check your internet connection and try again.'));
              } else if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
                window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
              } else {
                console.error('unexpected error loading app logs', error);
                dispatch(finishLoading('Unexpected error when loading app logs. Please try again.'));
              }
            } else {
              dispatch(finishLoading(undefined, snapshot));
            }
          }
        );
      }

      return () => {
        if (subscription) {
          subscription.unsubscribe();
          dispatch(reset());
        }
      };
    },
    [
      mainServiceEnvironmentId
    ]
  );

  return (
    /* min-h-0 lets the log well take the remaining height of the scroll
       container instead of the old hard-coded `100vh - 50px - 12rem`, which
       assumed the desktop navbar and the desktop padding. */
    <div className="flex-1 min-h-0 flex flex-col gap-4">
      <div className="flex flex-col gap-1.5 flex-none">
        <Eyebrow>logs</Eyebrow>
        <h1 className="font-sora text-ds-20 font-semibold tracking-[-0.02em] leading-[1.2] text-white truncate">
          {app.name}
        </h1>
      </div>

      <SurfaceCard tone="surface" className="flex-1 min-h-[24rem] flex flex-col overflow-hidden">
        <div className="flex-none px-3.5 py-2 bg-navy-900 border-b border-navy-700 flex items-center justify-between gap-3">
          <span className="font-courrier text-ds-115 text-navy-300">container output</span>
          {mainServiceEnvironmentId ? (
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-state-ok-soft" />
              <span className="font-courrier text-ds-105 uppercase tracking-[0.14em] text-state-ok-soft">live</span>
            </span>
          ) : null}
        </div>

        {isLoading && mainServiceEnvironmentId ? (
          <div className="flex flex-col justify-center items-center flex-1 bg-navy-950">
            <LoadingSpinner />
            <div className="mt-4 text-ds-135 text-navy-200">Loading logs</div>
          </div>
        ) : (
          <div className="flex-1 min-h-0">
            <ContainerLogBox isLoading={isLoading} errorMessage={loadingErrorMessage} logs={logs} canMaximize fullHeight />
          </div>
        )}
      </SurfaceCard>
    </div>
  );
};

export default MyAppLogs;
